import { Switch } from '@odekoteam/doppio';
import { useState } from 'react';
import { UPDATE_LOCATIONS_PRINT_DELIVERY_SHEET_ENABLED } from 'src/graphql/requests/updateLocationsPrintDeliverySheetEnabled';
import { GUpdateLocationsPrintDeliverySheetEnabledMutation } from 'src/graphql/types/generated';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { routingLocationsSelector } from 'src/state/selectors/locations';
import { updateRoutingLocations } from 'src/state/slices/locations';
import { Flex } from 'theme-ui';
import { useMutation } from 'urql';

export const printDeliverySheetSwitchId = (locationId: string): string =>
  `print-delivery-sheet-switch-${locationId}`;

// Plain per-location opt-in. State is DERIVED from the routing-locations slice
// (optimistically updated here) rather than held locally, so a bulk edit and an
// individual toggle both reflect immediately in the table. Mirrors the enforce
// item scanning switch but reconciles/rolls back off the mutation promise so a
// failed write cannot leave the switch stuck.
const PrintDeliverySheetSwitch = ({
  locationId,
  value,
}: {
  locationId: string;
  value?: boolean;
}): JSX.Element => {
  const routingLocations = useAppSelector(routingLocationsSelector);
  const dispatch = useAppDispatch();
  const locationInState = routingLocations.find(
    (location) => location.locationId === locationId,
  );
  // The doppio Switch is only controlled while isChecked is a boolean; an
  // undefined makes it uncontrolled, so it would move on click with nothing
  // wired behind it.
  const isChecked = Boolean(locationInState?.printDeliverySheetEnabled ?? value);
  const [isDisabled, setIsDisabled] = useState(false);
  const [, updateLocationsPrintDeliverySheetEnabled] =
    useMutation<GUpdateLocationsPrintDeliverySheetEnabledMutation>(
      UPDATE_LOCATIONS_PRINT_DELIVERY_SHEET_ENABLED,
    );

  const setFlag = (next: boolean): void => {
    dispatch(
      updateRoutingLocations([{ locationId, printDeliverySheetEnabled: next }]),
    );
  };

  const handleSwitch = (newValue: boolean): void => {
    if (isDisabled) return;
    const previous = isChecked;
    // optimistic + debounce
    setFlag(newValue);
    setIsDisabled(true);
    updateLocationsPrintDeliverySheetEnabled({
      input: { locationIds: [locationId], printDeliverySheetEnabled: newValue },
    })
      .then((result) => {
        const confirmed =
          result.data?.updateLocationsPrintDeliverySheetEnabled?.locations[0]
            ?.printDeliverySheetEnabled;
        // Revert on error or an incomplete payload; otherwise reconcile with
        // the server value.
        const failed = Boolean(result.error) || confirmed === undefined;
        setFlag(failed ? previous : confirmed);
        setIsDisabled(false);
      })
      .catch(() => {
        setFlag(previous);
        setIsDisabled(false);
      });
  };

  return (
    <Flex>
      <Switch
        id={printDeliverySheetSwitchId(locationId)}
        accessibilityLabel="print delivery sheet toggle"
        onCheckedChange={handleSwitch}
        size="default"
        isChecked={isChecked}
      />
      <span sx={{ marginLeft: '12px', variant: 'text.paragraphLgDefault' }}>
        {isChecked ? 'On' : 'Off'}
      </span>
    </Flex>
  );
};

export default PrintDeliverySheetSwitch;
